import React from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Telescope, LogOut, Zap, Menu, X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { getProfile } from '../services/api';

const Header: React.FC = () => {
  const { isAuthenticated, displayName, signOut, signIn, getIdToken } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();

  // Stacking-minutes balance chip. Fetched once per header mount so
  // navigating between pages doesn't spam /profile; the Dashboard tile
  // is authoritative for a live reading. Failure is non-fatal — the
  // chip just doesn't render.
  const [balance, setBalance] = React.useState<number | null>(null);
  React.useEffect(() => {
    if (!isAuthenticated) return;
    let cancelled = false;
    (async () => {
      try {
        const token = await getIdToken();
        if (!token) return;
        const p = await getProfile({ token });
        if (!cancelled) setBalance(p.stackingMinutes.balance);
      } catch {
        /* non-fatal — chip stays hidden */
      }
    })();
    return () => { cancelled = true; };
  }, [isAuthenticated, getIdToken]);

  // Mobile menu open/close. Auto-close on route change so tapping a link
  // in the drawer doesn't leave it hanging over the destination page.
  const [menuOpen, setMenuOpen] = React.useState(false);
  React.useEffect(() => { setMenuOpen(false); }, [loc.pathname]);

  const balanceChip = balance !== null && (
    <Link
      to="/profile"
      title={`${balance.toLocaleString()} stacking minutes remaining`}
      className={`whitespace-nowrap inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-medium transition-colors ${
        balance < 0
          ? 'border-red-400/40 bg-red-500/10 text-red-200 hover:bg-red-500/15'
          : balance < 30
          ? 'border-amber-400/40 bg-amber-500/10 text-amber-200 hover:bg-amber-500/15'
          : 'border-white/10 bg-white/[0.04] text-white/75 hover:bg-white/[0.08]'
      }`}
    >
      <Zap className="h-3.5 w-3.5" />
      <span className="tabular-nums">{balance.toLocaleString()}</span>
      <span className="opacity-60 hidden sm:inline">min</span>
    </Link>
  );

  return (
    <header className="relative z-30 px-4 py-4 md:px-10 md:py-5">
      <div className="flex items-center justify-between">
        <Link to={isAuthenticated ? '/dashboard' : '/'} className="flex items-center gap-2 group">
          <div className="relative">
            <Telescope className="h-6 w-6 text-nebula-300 group-hover:text-nebula-200 transition-colors" />
            <div className="absolute inset-0 blur-xl bg-nebula-500/40 -z-10" />
          </div>
          <span className="font-display text-lg font-semibold tracking-tight">
            Pancake<span className="text-nebula-300">Stack</span>
          </span>
        </Link>

        {/* Desktop nav — hidden below md, replaced by the hamburger. */}
        <nav className="hidden md:flex items-center gap-4">
          {isAuthenticated && (
            <>
              <button
                onClick={() => nav('/collections')}
                className="whitespace-nowrap text-sm text-white/70 hover:text-white transition-colors px-3 py-1.5"
              >
                Collections
              </button>
              <button
                onClick={() => nav('/jobs')}
                className="whitespace-nowrap text-sm text-white/70 hover:text-white transition-colors px-3 py-1.5"
              >
                Stacks
              </button>
            </>
          )}
          <Link
            to="/try"
            className="whitespace-nowrap text-sm text-white/70 hover:text-white transition-colors px-3 py-1.5"
          >
            FITS-preview
          </Link>
          <a
            href="/sky/"
            className="whitespace-nowrap text-sm text-white/70 hover:text-white transition-colors px-3 py-1.5"
          >
            Sky
          </a>
          <Link
            to="/api-docs"
            className="whitespace-nowrap text-sm text-white/70 hover:text-white transition-colors px-3 py-1.5"
          >
            API
          </Link>
          {isAuthenticated ? (
            <>
              {balanceChip}
              <button
                onClick={() => nav('/profile')}
                className="whitespace-nowrap text-sm text-white/50 hover:text-white transition-colors"
              >
                {displayName()}
              </button>
              <button
                onClick={signOut}
                aria-label="Sign out"
                title="Sign out"
                className="flex items-center gap-1.5 whitespace-nowrap text-sm text-white/70 hover:text-white transition-colors px-3 py-1.5"
              >
                <LogOut className="h-4 w-4" />
                <span>Sign out</span>
              </button>
            </>
          ) : (
            <button
              onClick={signIn}
              className="whitespace-nowrap px-4 py-2 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur text-sm font-medium transition-colors border border-white/10"
            >
              Sign in
            </button>
          )}
        </nav>

        {/* Mobile controls — balance chip stays visible so the user can
            see their minutes without opening the drawer; everything else
            collapses behind the hamburger. */}
        <div className="flex md:hidden items-center gap-2">
          {isAuthenticated && balanceChip}
          <button
            onClick={() => setMenuOpen(v => !v)}
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={menuOpen}
            className="inline-flex items-center justify-center h-9 w-9 rounded-md border border-white/10 bg-white/5 text-white/80 hover:bg-white/10"
          >
            {menuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {/* Mobile drawer — inline under the header row. Renders the same
          links as the desktop nav, one per line for tap-target comfort. */}
      {menuOpen && (
        <nav className="md:hidden mt-3 rounded-xl border border-white/10 bg-black/60 backdrop-blur p-2 flex flex-col">
          {isAuthenticated && (
            <>
              <MobileItem onClick={() => nav('/collections')}>Collections</MobileItem>
              <MobileItem onClick={() => nav('/jobs')}>Stacks</MobileItem>
            </>
          )}
          <MobileItem onClick={() => nav('/try')}>FITS-preview</MobileItem>
          <MobileItem onClick={() => { window.location.href = '/sky/'; }}>Sky</MobileItem>
          <MobileItem onClick={() => nav('/api-docs')}>API</MobileItem>
          {isAuthenticated ? (
            <>
              <div className="h-px bg-white/10 my-1" />
              <MobileItem onClick={() => nav('/profile')}>
                <span className="text-white/60">Signed in as</span>{' '}
                <span className="text-white">{displayName()}</span>
              </MobileItem>
              <MobileItem onClick={signOut}>
                <span className="inline-flex items-center gap-2">
                  <LogOut className="h-4 w-4" /> Sign out
                </span>
              </MobileItem>
            </>
          ) : (
            <MobileItem onClick={signIn}>Sign in</MobileItem>
          )}
        </nav>
      )}
    </header>
  );
};

const MobileItem: React.FC<React.PropsWithChildren<{ onClick: () => void }>> = ({ onClick, children }) => (
  <button
    onClick={onClick}
    className="text-left text-sm text-white/80 hover:text-white hover:bg-white/5 rounded-md px-3 py-2.5"
  >
    {children}
  </button>
);

export default Header;
